/**
 * ChartMapTool - 도형표현도 도구
 * 피처 위치에 파이/막대 그래프를 표시
 */

import { getCenter } from 'ol/extent';
import { fromLonLat } from 'ol/proj';
import GeoJSON from 'ol/format/GeoJSON';
import * as turf from '@turf/turf';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import { Style, Icon } from 'ol/style';
import { layerManager } from '../core/LayerManager.js';
import { collectNumericFields } from '../utils/layerSelect.js';
import { mapManager } from '../core/MapManager.js';
import { eventBus, Events } from '../utils/EventBus.js';
import { makeDraggable } from '../utils/DraggableElement.js';
import { syncLegendVisibility } from './legendVisibility.js';

class ChartMapTool {
  constructor() {
    // 차트는 HTML 오버레이가 아니라 피처에 구운 SVG 아이콘으로 지도 캔버스에 그린다.
    // → 레이어 가시성/순서/저장(.egis)/이미지 내보내기/스토리맵이 일반 벡터와 동일하게 동작.
    this.iconStyleCache = new Map();    // svg dataURL -> ol Style (프레임마다 재생성 방지)
    this.legends = new Map();           // derivedLayerId -> legend element
    // 한 레이어로 도형표현도를 여러 번 만들 수 있다 — 적용할 때마다 레이어가 하나씩 늘어난다.
    this.derivedBySource = new Map();   // sourceLayerId -> Set<derivedLayerId>
    this.sourceByDerived = new Map();   // derivedLayerId -> sourceLayerId
    this.geoJSONFormat = new GeoJSON(); // 무게중심 계산용 (OL ↔ turf)
    this.colors = [
      '#3b82f6', '#ef4444', '#10b981', '#f59e0b',
      '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16',
      '#f97316', '#14b8a6', '#a855f7', '#f43f5e'
    ];

    this.initEventListeners();
  }

  /**
   * 이벤트 리스너 초기화
   * - 파생 레이어 삭제: 오버레이/범례 제거
   * - 원본 레이어 삭제: 연결된 파생 레이어도 제거
   * - 파생 레이어 가시성 토글: 오버레이 표시/숨김
   */
  initEventListeners() {
    eventBus.on(Events.LAYER_REMOVED, (data) => {
      if (!data || !data.layerId) return;
      const { layerId } = data;

      if (this.sourceByDerived.has(layerId)) {
        // 파생 레이어가 삭제됨 → 연결만 끊고 오버레이 정리 (같은 원본의 다른 도형표현도는 유지)
        this.unlinkDerived(layerId);
        this.cleanupOverlays(layerId);
      } else if (this.derivedBySource.has(layerId)) {
        // 원본이 삭제됨 → 원본에서 만든 도형표현도 레이어를 모두 제거
        const derivedIds = Array.from(this.derivedBySource.get(layerId));
        this.derivedBySource.delete(layerId);
        derivedIds.forEach(derivedId => {
          this.sourceByDerived.delete(derivedId);
          layerManager.removeLayer(derivedId);
        });
      }
    });

    eventBus.on(Events.LAYER_VISIBILITY_CHANGED, (data) => {
      if (!data || !data.layerId) return;
      // 차트 자체는 레이어(피처)라 OL 가시성이 처리 — 범례만 함께 토글
      const legendEl = this.legends.get(data.layerId);
      if (legendEl) legendEl.style.display = data.visible ? '' : 'none';
    });

    // 레이어 이름 변경 → 범례 제목 동기화
    eventBus.on(Events.LAYER_RENAMED, (data) => {
      if (!data || !data.layerId) return;
      this.onLayerRenamed(data.layerId, data.name);
    });
  }

  /**
   * 파생(도형표현도) 레이어 이름 변경 시 범례 제목을 새 이름으로 갱신.
   * _chartMapConfig.title에도 반영해 재생성·복원 후에도 유지된다.
   */
  onLayerRenamed(layerId, name) {
    const legendEl = this.legends.get(layerId);
    if (!legendEl) return;
    const titleEl = legendEl.querySelector('.chart-legend-title');
    if (titleEl && titleEl.textContent !== name) titleEl.textContent = name;
    const layerInfo = layerManager.getLayer(layerId);
    if (layerInfo && layerInfo._chartMapConfig) layerInfo._chartMapConfig.title = name;
  }

  /** 원본 ↔ 파생 레이어 연결 등록 (한 원본에 여러 개) */
  linkDerived(sourceLayerId, derivedLayerId) {
    if (!this.derivedBySource.has(sourceLayerId)) {
      this.derivedBySource.set(sourceLayerId, new Set());
    }
    this.derivedBySource.get(sourceLayerId).add(derivedLayerId);
    this.sourceByDerived.set(derivedLayerId, sourceLayerId);
  }

  /** 파생 레이어 연결 해제 */
  unlinkDerived(derivedLayerId) {
    const sourceId = this.sourceByDerived.get(derivedLayerId);
    this.sourceByDerived.delete(derivedLayerId);
    if (!sourceId) return;
    const set = this.derivedBySource.get(sourceId);
    if (!set) return;
    set.delete(derivedLayerId);
    if (set.size === 0) this.derivedBySource.delete(sourceId);
  }

  /** 이 원본에서 만든 도형표현도 레이어 id 목록 */
  getDerivedIds(sourceLayerId) {
    const set = this.derivedBySource.get(sourceLayerId);
    return set ? Array.from(set) : [];
  }

  /** 같은 이름이 이미 있으면 뒤에 번호를 붙인다 (도형표현도를 여러 번 만들 수 있으므로) */
  uniqueLayerName(base) {
    const taken = new Set(layerManager.getAllLayers().map(l => l.name));
    if (!taken.has(base)) return base;
    let n = 2;
    while (taken.has(`${base} ${n}`)) n++;
    return `${base} ${n}`;
  }

  cleanupOverlays(derivedLayerId) {
    // 차트 피처는 레이어 제거/재렌더 때 source가 함께 정리된다 — 범례만 치운다
    this.removeLegend(derivedLayerId);
  }

  /** 데이터 URL 속 SVG에 xmlns가 없으면 주입 — xmlns 없는 SVG는 이미지(Icon)로
   *  렌더되지 않는다(브라우저 규칙). 초기 버전이 구운 파일 하위 호환용. */
  ensureSvgXmlns(dataUrl) {
    const prefix = 'data:image/svg+xml;utf8,';
    if (typeof dataUrl !== 'string' || !dataUrl.startsWith(prefix)) return dataUrl;
    const svg = decodeURIComponent(dataUrl.slice(prefix.length));
    if (/<svg[^>]*\sxmlns=/.test(svg)) return dataUrl;
    return prefix + encodeURIComponent(svg.replace('<svg ', '<svg xmlns="http://www.w3.org/2000/svg" '));
  }

  /** 피처에 구운 차트 SVG(_chartSvg)를 아이콘으로 그리는 스타일 함수. */
  chartIconStyle = (feature) => {
    const src = feature.get('_chartSvg');
    if (!src) return null;
    let style = this.iconStyleCache.get(src);
    if (!style) {
      style = new Style({ image: new Icon({ src: this.ensureSvgXmlns(src) }) });
      this.iconStyleCache.set(src, style);
    }
    return style;
  };

  /**
   * 도형표현도 생성
   */
  createChartMap(layerId, options) {
    const config = {
      chartType: options.chartType || 'pie', // 'pie' | 'bar' | '100bar'
      fields: options.fields || [],          // 표시할 숫자 필드들
      sizeField: options.sizeField || null,  // 크기 기준 필드 (없으면 고정 크기)
      minSize: options.minSize !== undefined ? options.minSize : 20,
      maxSize: options.maxSize !== undefined ? options.maxSize : 60,
      showLabels: options.showLabels || false,
      // 필드별 색(fields와 같은 순서). 없으면 기본 팔레트.
      colors: Array.isArray(options.colors) && options.colors.length ? options.colors : null,
      // 수치 라벨: 파이·100%막대=%, 막대=절댓값
      showValues: !!options.showValues
    };

    const layerInfo = layerManager.getLayer(layerId);
    if (!layerInfo) {
      throw new Error('레이어를 찾을 수 없습니다.');
    }

    // 흐름·래스터처럼 벡터 소스가 없는 레이어는 도형표현도를 만들 수 없다
    const sourceOfLayer = layerInfo.source || (layerInfo.olLayer.getSource && layerInfo.olLayer.getSource());
    if (!sourceOfLayer || typeof sourceOfLayer.getFeatures !== 'function') {
      throw new Error('벡터 레이어가 아닙니다.');
    }
    const features = sourceOfLayer.getFeatures();
    if (features.length === 0) {
      throw new Error('레이어에 피처가 없습니다.');
    }

    // 적용할 때마다 새 레이어를 만든다 — 기존 도형표현도는 그대로 두고 나란히 쌓인다.
    // (필요 없으면 레이어 패널에서 지우면 된다)
    const placeholderSource = new VectorSource();
    const placeholderLayer = new VectorLayer({ source: placeholderSource });

    const derivedLayerId = layerManager.addLayer({
      name: this.buildDerivedName(layerInfo.name, config),
      type: 'chartmap',
      geometryType: 'Point',
      olLayer: placeholderLayer,
      source: placeholderSource,
      visible: true
    });

    // 설정을 파생 레이어에 보관 (복원용 직렬화 대상)
    const derivedLayer = layerManager.getLayer(derivedLayerId);
    if (derivedLayer) {
      derivedLayer._chartMapConfig = { sourceLayerId: layerId, ...config };
    }

    this.linkDerived(layerId, derivedLayerId);

    // 오버레이/범례 렌더링
    const chartCount = this.renderChartMap(derivedLayerId, layerId, config);

    return {
      layerId: derivedLayerId,
      sourceLayerId: layerId,
      chartCount,
      chartType: config.chartType
    };
  }

  /**
   * 저장된 도형표현도 복원 (파생 레이어는 이미 추가된 상태)
   * @param {string} derivedLayerId - 복원된 파생 레이어 ID
   * @param {string} sourceLayerId - 원본 레이어 ID
   * @param {object} config - 저장된 차트 설정
   */
  restoreChartMap(derivedLayerId, sourceLayerId, config) {
    const sourceInfo = layerManager.getLayer(sourceLayerId);
    if (!sourceInfo) return 0; // 원본이 없으면 복원 불가

    const derivedLayer = layerManager.getLayer(derivedLayerId);
    if (derivedLayer) {
      derivedLayer._chartMapConfig = { sourceLayerId, ...config };
    }

    this.linkDerived(sourceLayerId, derivedLayerId);

    return this.renderChartMap(derivedLayerId, sourceLayerId, config);
  }

  /**
   * 차트 오버레이 + 범례 렌더링 (생성/복원 공통)
   * @returns {number} 생성된 차트 수
   */
  renderChartMap(derivedLayerId, sourceLayerId, config) {
    const { chartType, fields, sizeField, minSize, maxSize } = config;
    const colors = (config.colors && config.colors.length) ? config.colors : this.colors;
    const showValues = !!config.showValues;

    const sourceInfo = layerManager.getLayer(sourceLayerId);
    if (!sourceInfo) return 0;

    const derivedInfo = layerManager.getLayer(derivedLayerId);
    if (!derivedInfo || !derivedInfo.source) return 0;

    const sourceOfLayer = sourceInfo.source || (sourceInfo.olLayer.getSource && sourceInfo.olLayer.getSource());
    if (!sourceOfLayer || typeof sourceOfLayer.getFeatures !== 'function') return 0;
    const features = sourceOfLayer.getFeatures();

    // 기존 범례 정리 (재렌더 대비 — 차트 피처는 아래에서 source.clear로 교체)
    this.cleanupOverlays(derivedLayerId);

    // 지표가 1개면 구성비를 그릴 수 없다. 대신
    //  - 파이: 원의 크기로 값을 표현 (비례원)
    //  - 100% 막대: 전체 피처의 최댓값을 막대 전체 높이로 두고 그만큼만 채움
    const singleField = fields.length === 1;
    const sizeByValueField = (chartType === 'pie' && singleField && !sizeField) ? fields[0] : null;
    const effectiveSizeField = sizeField || sizeByValueField;

    // 크기 범위 계산
    let minValue = Infinity, maxValue = -Infinity;
    if (effectiveSizeField) {
      features.forEach(f => {
        const val = parseFloat(f.get(effectiveSizeField));
        if (!isNaN(val)) {
          minValue = Math.min(minValue, val);
          maxValue = Math.max(maxValue, val);
        }
      });
    }

    // 전체 최대값 계산 (각 필드별) — 막대 차트, 그리고 지표 1개짜리 100% 막대에서 쓴다
    const globalMaxValues = {};
    if (chartType === 'bar' || (chartType === '100bar' && singleField)) {
      fields.forEach(field => {
        let fieldMax = 0;
        features.forEach(f => {
          const val = parseFloat(f.get(field)) || 0;
          if (val > fieldMax) fieldMax = val;
        });
        globalMaxValues[field] = fieldMax;
      });
    }

    // 막대 너비·라벨 글자 크기의 기준 — 피처마다 달라지지 않게 설정값으로 한 번만 정한다
    const refSize = (minSize + maxSize) / 2;

    const chartFeatures = [];

    features.forEach((feature) => {
      // 피처 위치 계산 (폴리곤은 무게중심)
      const center = this.getChartPosition(feature);

      // 필드 값 추출
      const values = fields.map(f => parseFloat(feature.get(f)) || 0);
      const total = values.reduce((a, b) => a + b, 0);

      if (total === 0) return; // 값이 없으면 스킵

      // 크기 계산
      let size = (minSize + maxSize) / 2;
      if (effectiveSizeField && maxValue > minValue) {
        const sizeVal = parseFloat(feature.get(effectiveSizeField)) || 0;
        const ratio = (sizeVal - minValue) / (maxValue - minValue);
        size = minSize + ratio * (maxSize - minSize);
      }

      // 차트 SVG 생성
      // 막대류는 refSize로 너비·글자 크기를 고정한다 — 값에 따라 변하는 건 길이(높이)뿐.
      let svg;
      if (chartType === 'pie') {
        svg = this.createPieChart(values, fields, size, colors, showValues);
      } else if (chartType === '100bar') {
        svg = singleField
          // 지표 1개: 최댓값을 막대 전체로 두고 그 값만큼 채운다
          ? this.createSingleBarChart(values[0], globalMaxValues[fields[0]], size, colors[0], showValues, refSize)
          // 100% 누적 막대: 피처별 합계를 100%로 정규화
          : this.createStackedBar100Chart(values, fields, size, colors, showValues, refSize);
      } else {
        // 막대 차트는 전체 최대값 기준으로 높이 계산
        svg = this.createBarChart(values, fields, size, fields.map(f => globalMaxValues[f]), colors, showValues, refSize);
      }
      if (!svg) return;

      // 차트를 피처에 굽는다 — 지도 캔버스에 그려져 .egis 저장·스토리맵·이미지 내보내기에 그대로 실림
      const chartFeature = new Feature({ geometry: new Point(center) });
      chartFeature.set('_chartSvg', 'data:image/svg+xml;utf8,' + encodeURIComponent(svg));
      chartFeature.set('_chartSize', size);
      chartFeatures.push(chartFeature);
    });

    this.iconStyleCache.clear(); // 재렌더 시 이전 아이콘 캐시 정리
    derivedInfo.source.clear();
    derivedInfo.source.addFeatures(chartFeatures);
    derivedInfo.olLayer.setStyle(this.chartIconStyle);
    derivedInfo.geometryType = 'Point';

    // 범례 생성 (파생 레이어 기준)
    this.createLegend(derivedLayerId, sourceInfo.name, chartType, fields, globalMaxValues, colors, {
      sizeRange: sizeByValueField && maxValue > minValue ? { min: minValue, max: maxValue } : null
    });

    // 파생 레이어가 숨김 상태면 범례도 숨김 (차트는 레이어 가시성이 처리)
    if (derivedInfo.visible === false) {
      const legendEl = this.legends.get(derivedLayerId);
      if (legendEl) legendEl.style.display = 'none';
    }

    return chartFeatures.length;
  }

  /**
   * 차트를 표시할 위치 계산
   * - 포인트: 좌표 그대로
   * - 폴리곤: 면적가중 무게중심(center of mass)
   * - 멀티폴리곤(본토+섬 등): 면적이 가장 큰 덩어리(본토)의 무게중심
   *   → 흩어진 섬들 때문에 무게중심이 바다로 빠지는 것을 방지
   * - 무게중심이 폴리곤 밖(오목/구멍)에 떨어지면 표면 위 점으로 보정
   * 실패 시 경계상자 중심으로 폴백.
   * @returns {number[]} 지도 좌표(EPSG:3857)
   */
  getChartPosition(feature) {
    const geometry = feature.getGeometry();
    const type = geometry.getType();

    if (type === 'Point') {
      return geometry.getCoordinates();
    }

    try {
      // OL(EPSG:3857) → GeoJSON(EPSG:4326)
      const geoJSONGeom = this.geoJSONFormat.writeGeometryObject(geometry, {
        dataProjection: 'EPSG:4326',
        featureProjection: 'EPSG:3857'
      });

      // 멀티폴리곤이면 면적이 가장 큰 폴리곤(본토)만 사용
      let target = geoJSONGeom;
      if (geoJSONGeom.type === 'MultiPolygon') {
        target = this.largestPolygon(geoJSONGeom) || geoJSONGeom;
      }

      const com = turf.centerOfMass(target);
      let coords = com && com.geometry && com.geometry.coordinates;

      // 무게중심이 폴리곤 밖이면 표면 위 점으로 보정
      try {
        if (target.type === 'Polygon' && (!coords || !turf.booleanPointInPolygon(com, target))) {
          const pof = turf.pointOnFeature(target);
          coords = pof.geometry.coordinates;
        }
      } catch (e) {
        // booleanPointInPolygon 등 실패 시 com 좌표 유지
      }

      if (coords && isFinite(coords[0]) && isFinite(coords[1])) {
        return fromLonLat(coords); // 다시 EPSG:3857로
      }
    } catch (e) {
      // 폴백
    }

    return getCenter(geometry.getExtent());
  }

  /**
   * 멀티폴리곤에서 면적이 가장 큰 폴리곤 반환 (GeoJSON Polygon)
   */
  largestPolygon(multiPolygon) {
    let best = null;
    let bestArea = -Infinity;
    multiPolygon.coordinates.forEach(rings => {
      try {
        const poly = turf.polygon(rings);
        const area = turf.area(poly);
        if (area > bestArea) {
          bestArea = area;
          best = poly.geometry;
        }
      } catch (e) {
        // 잘못된 링은 스킵
      }
    });
    return best;
  }

  /**
   * 범례 생성
   */
  createLegend(layerId, layerName, chartType, fields, globalMaxValues, colors = this.colors, extra = {}) {
    // 기존 범례 제거
    this.removeLegend(layerId);

    const legendEl = document.createElement('div');
    legendEl.className = 'chart-map-legend';
    legendEl.id = `chart-legend-${layerId}`;

    // 이름 변경으로 저장된 커스텀 제목이 있으면 그것을, 없으면 기본 제목을 사용
    const renamedInfo = layerManager.getLayer(layerId);
    const customTitle = renamedInfo && renamedInfo._chartMapConfig && renamedInfo._chartMapConfig.title;
    const titleText = customTitle || `${layerName} - ${this.getChartTypeLabel(chartType)} 차트`;
    let legendHTML = '<div class="chart-legend-title" contenteditable="plaintext-only" spellcheck="false"></div>';
    legendHTML += '<div class="chart-legend-items">';

    fields.forEach((field, i) => {
      const color = colors[i % colors.length];
      let label = field;

      // 막대 차트(그리고 지표 1개짜리 100% 막대)는 채움 기준인 최대값을 함께 표시
      const singleField = fields.length === 1;
      if ((chartType === 'bar' || (chartType === '100bar' && singleField)) &&
          globalMaxValues && globalMaxValues[field]) {
        const maxVal = globalMaxValues[field];
        label += ` (최대: ${this.formatNumber(maxVal)})`;
      } else if (chartType === 'pie' && singleField && extra.sizeRange) {
        // 지표 1개 파이 = 비례원. 원 크기가 무엇을 뜻하는지 밝힌다.
        label += ` (원 크기: ${this.formatNumber(extra.sizeRange.min)} ~ ${this.formatNumber(extra.sizeRange.max)})`;
      }

      legendHTML += `
        <div class="chart-legend-item">
          <span class="chart-legend-color" style="background:${color}"></span>
          <span class="chart-legend-label">${label}</span>
        </div>`;
    });

    legendHTML += '</div>';
    legendEl.innerHTML = legendHTML;
    const titleEl = legendEl.querySelector('.chart-legend-title');
    titleEl.textContent = titleText;
    // 제목 편집 → 설정 저장(라이브) + 편집 완료(blur/Enter) 시 레이어 이름 동기화(양방향)
    titleEl.addEventListener('input', () => {
      const info = layerManager.getLayer(layerId);
      if (info && info._chartMapConfig) info._chartMapConfig.title = titleEl.textContent;
    });
    titleEl.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); titleEl.blur(); }
    });
    titleEl.addEventListener('blur', () => {
      const name = titleEl.textContent.trim();
      const info = layerManager.getLayer(layerId);
      if (name && info && name !== info.name) layerManager.renameLayer(layerId, name);
    });

    // 지도 컨테이너에 추가
    const mapContainer = document.getElementById('map');
    if (mapContainer) {
      mapContainer.appendChild(legendEl);
      this.legends.set(layerId, legendEl);
      makeDraggable(legendEl, () => mapContainer);
      // 복원처럼 숨긴 레이어의 범례를 만들 때도 레이어 가시성을 따른다
      syncLegendVisibility(layerId);
    }
  }

  /**
   * 숫자 포맷
   */
  formatNumber(num) {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toLocaleString();
  }

  /**
   * 범례 제거
   */
  removeLegend(layerId) {
    const legendEl = this.legends.get(layerId);
    if (legendEl) {
      legendEl.remove();
      this.legends.delete(layerId);
    }
  }

  /**
   * 파이 차트 SVG 생성
   */
  /**
   * 수치 라벨 글자 크기 — 설정된 크기 기준(refSize)으로 정한다.
   * 피처 크기에 비례시키면 작은 피처의 숫자가 읽을 수 없이 작아진다.
   */
  valueFontSize(refSize) {
    return Math.max(12, Math.round(refSize * 0.28));
  }

  /** 라벨이 차지할 대략적인 가로 폭 — SVG 폭을 정해 글자가 잘리지 않게 한다. */
  estimateLabelWidth(text, fontSize) {
    return String(text).length * fontSize * 0.62 + fontSize * 0.5;
  }

  /** 수치 라벨용 SVG <text> — 흰 글자 + 검은 외곽선(paint-order)로 어떤 색 위에서도 읽힘. */
  valueLabelSvg(x, y, text, fontSize) {
    return `<text x="${x}" y="${y}" text-anchor="middle" dominant-baseline="middle"
            font-family="sans-serif" font-weight="bold" font-size="${fontSize}"
            fill="#fff" stroke="#000" stroke-width="${(fontSize * 0.18).toFixed(1)}" paint-order="stroke">${text}</text>`;
  }

  createPieChart(values, fields, size, colors = this.colors, showValues = false) {
    const total = values.reduce((a, b) => a + b, 0);
    if (total === 0) return '';

    const cx = size / 2;
    const cy = size / 2;
    const r = size / 2 - 2;
    const fs = Math.max(7, Math.round(size * 0.16));

    // 값이 하나뿐이면 조각이 아니라 원 하나로 그린다.
    // 지표 1개일 때는 원의 크기(반지름)가 값을 나타낸다(비례원).
    // SVG 호(A)는 시작점과 끝점이 같으면 아무것도 그려지지 않아 원으로 그려야 한다.
    const onlyIndex = values.findIndex(v => v > 0);
    if (values.filter(v => v > 0).length === 1) {
      const circle = `<circle cx="${cx}" cy="${cy}" r="${r}"
                fill="${colors[onlyIndex % colors.length]}" stroke="white" stroke-width="1"/>`;
      const label = showValues
        ? this.valueLabelSvg(cx, cy, values.length === 1 ? this.formatNumber(values[onlyIndex]) : '100%', fs)
        : '';
      return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">${circle}${label}</svg>`;
    }

    let paths = '';
    let labels = '';
    let startAngle = -Math.PI / 2;

    values.forEach((value, i) => {
      if (value <= 0) return;

      const angle = (value / total) * 2 * Math.PI;
      const endAngle = startAngle + angle;

      const x1 = cx + r * Math.cos(startAngle);
      const y1 = cy + r * Math.sin(startAngle);
      const x2 = cx + r * Math.cos(endAngle);
      const y2 = cy + r * Math.sin(endAngle);

      const largeArc = angle > Math.PI ? 1 : 0;

      paths += `<path d="M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z"
                fill="${colors[i % colors.length]}"
                stroke="white" stroke-width="1"/>`;

      // 수치 라벨(%): 조각 중간 각도, 반지름 0.6 지점. 5% 미만 조각은 겹침 방지 생략
      const pct = Math.round((value / total) * 100);
      if (showValues && pct >= 5) {
        const mid = startAngle + angle / 2;
        labels += this.valueLabelSvg(cx + r * 0.6 * Math.cos(mid), cy + r * 0.6 * Math.sin(mid), `${pct}%`, fs);
      }

      startAngle = endAngle;
    });

    return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">${paths}${labels}</svg>`;
  }

  /**
   * 막대 차트 SVG 생성
   * @param {number[]} values - 각 필드의 값
   * @param {string[]} fields - 필드 이름 목록
   * @param {number} size - 차트 크기
   * @param {number[]} globalMaxValues - 각 필드의 전체 최대값 (선택)
   */
  createBarChart(values, fields, size, globalMaxValues = null, colors = this.colors, showValues = false, refSize = size) {
    // 전체 최대값이 제공되면 그것을 사용, 아니면 현재 값 중 최대값 사용
    const maxVals = globalMaxValues || values.map(() => Math.max(...values));
    const overallMax = Math.max(...maxVals);
    if (overallMax === 0) return '';

    // 너비·글자 크기는 고정(refSize 기준) — 값에 따라 변하는 것은 막대 길이(높이)뿐
    const fs = this.valueFontSize(refSize);
    const labelSpace = showValues ? fs + 3 : 0; // 라벨은 막대 위 — 그만큼 막대 높이를 줄여 자리 확보

    const barWidth = Math.max(6, Math.round((refSize / 2) / values.length));
    const totalBarWidth = barWidth * values.length;

    // 바깥쪽 막대의 라벨이 잘리지 않게 캔버스를 넓힌다
    const maxLabelWidth = showValues
      ? Math.max(...values.map(v => this.estimateLabelWidth(this.formatNumber(v), fs)))
      : 0;
    const svgW = Math.max(totalBarWidth + 4, totalBarWidth + maxLabelWidth);
    const svgH = Math.max(size, fs + 10);

    const offsetX = (svgW - totalBarWidth) / 2;
    const maxHeight = svgH - 4 - labelSpace;

    const baselineY = svgH - 2;

    let bars = '';
    let labels = '';
    values.forEach((value, i) => {
      // 각 막대는 해당 필드의 전체 최대값 기준으로 높이 계산
      const fieldMax = globalMaxValues ? globalMaxValues[i] : overallMax;
      const height = fieldMax > 0 ? (value / fieldMax) * maxHeight : 0;
      const x = offsetX + i * barWidth;
      const y = baselineY - height;

      bars += `<rect x="${x + 1}" y="${y}" width="${barWidth - 2}" height="${height}"
               fill="${colors[i % colors.length]}"
               stroke="white" stroke-width="2" stroke-linejoin="round"/>`;

      // 수치 라벨(절댓값): 막대 위
      if (showValues && value > 0) {
        labels += this.valueLabelSvg(x + barWidth / 2, y - fs * 0.6, this.formatNumber(value), fs);
      }
    });

    // 하단 가로축
    const axis = `<line x1="${offsetX - 1}" y1="${baselineY}" x2="${offsetX + totalBarWidth + 1}" y2="${baselineY}"
               stroke="#333" stroke-width="1.5"/>`;

    return `<svg xmlns="http://www.w3.org/2000/svg" width="${svgW}" height="${svgH}" viewBox="0 0 ${svgW} ${svgH}">
      ${axis}
      ${bars}
      ${labels}
    </svg>`;
  }

  /**
   * 100% 누적 막대 차트 SVG 생성
   * 피처별 합계를 100%로 정규화하여 각 필드의 구성비를 한 막대에 누적 표시.
   * @param {number[]} values - 각 필드의 값
   * @param {string[]} fields - 필드 이름 목록
   * @param {number} size - 차트 크기
   */
  createStackedBar100Chart(values, fields, size, colors = this.colors, showValues = false, refSize = size) {
    const total = values.reduce((a, b) => a + b, 0);
    if (total === 0) return '';

    // 너비·글자 크기는 고정(refSize 기준) — 값에 따라 변하는 것은 막대 길이(높이)뿐
    const barWidth = Math.max(8, Math.round(refSize / 3));
    const fs = this.valueFontSize(refSize);
    const svgW = barWidth + 4;
    const svgH = Math.max(size, fs + 8);
    const offsetX = (svgW - barWidth) / 2;
    const maxHeight = svgH - 4;

    const baselineY = 2 + maxHeight; // 막대 하단

    let segments = '';
    let labels = '';
    let yTop = 2; // 위에서부터 아래로 누적
    values.forEach((value, i) => {
      if (value <= 0) return;
      const h = (value / total) * maxHeight;
      segments += `<rect x="${offsetX}" y="${yTop}" width="${barWidth}" height="${h}"
               fill="${colors[i % colors.length]}"
               stroke="white" stroke-width="2" stroke-linejoin="round"/>`;
      // 수치 라벨(%): 구간 안에 들어갈 때만(작은 구간은 생략 — 겹침 방지)
      if (showValues && h >= fs + 2) {
        labels += this.valueLabelSvg(offsetX + barWidth / 2, yTop + h / 2, `${Math.round((value / total) * 100)}%`, fs);
      }
      yTop += h;
    });

    // 하단 가로축
    const axis = `<line x1="${offsetX - 2}" y1="${baselineY}" x2="${offsetX + barWidth + 2}" y2="${baselineY}"
               stroke="#333" stroke-width="1.5"/>`;

    return `<svg xmlns="http://www.w3.org/2000/svg" width="${svgW}" height="${svgH}" viewBox="0 0 ${svgW} ${svgH}">
      ${axis}
      ${segments}
      ${labels}
    </svg>`;
  }

  /**
   * 단일 지표 막대 SVG 생성 (100% 막대에서 지표가 1개일 때)
   * 막대 전체 높이 = 전체 피처의 최댓값. 그 값만큼만 아래에서부터 채운다.
   * @param {number} value - 이 피처의 값
   * @param {number} globalMax - 전체 피처 중 최대값 (막대 전체 높이 기준)
   * @param {number} size - 차트 크기
   * @param {string} color - 채움 색
   */
  createSingleBarChart(value, globalMax, size, color, showValues = false, refSize = size) {
    if (!globalMax || globalMax <= 0) return '';

    const ratio = Math.max(0, Math.min(1, value / globalMax));

    // 너비와 글자 크기는 고정 — 값에 따라 달라지는 것은 막대 길이(높이)뿐이다
    const barWidth = Math.max(8, Math.round(refSize / 3));
    const fs = this.valueFontSize(refSize);
    const pct = `${Math.round(ratio * 100)}%`;
    const labelWidth = showValues ? this.estimateLabelWidth(pct, fs) : 0;

    // 수치 라벨은 막대를 가리지 않게 막대 위에 둔다 — 그만큼 위쪽을 비워 놓는다
    const labelSpace = showValues ? fs + 4 : 0;
    const svgW = Math.max(barWidth + 4, labelWidth + 4);
    const svgH = Math.max(size, labelSpace + fs + 8);
    const offsetX = (svgW - barWidth) / 2;
    const topY = 2 + labelSpace;
    const maxHeight = svgH - 4 - labelSpace;
    const baselineY = topY + maxHeight;

    const h = ratio * maxHeight;
    const y = baselineY - h;

    // 값 막대
    const bar = h > 0
      ? `<rect x="${offsetX}" y="${y}" width="${barWidth}" height="${h}"
               fill="${color}" stroke="#333" stroke-width="1"/>`
      : '';

    // 최댓값까지 남은 만큼을 흰 막대(검은 테두리)로 값 막대 위에 얹는다 — 차이가 눈에 보인다.
    // 최댓값인 피처는 남은 칸이 없으므로 막대만 그려진다.
    const remain = maxHeight - h;
    const gapBar = remain > 0.5
      ? `<rect x="${offsetX}" y="${topY}" width="${barWidth}" height="${remain}"
               fill="#fff" stroke="#333" stroke-width="1"/>`
      : '';

    // 수치 라벨(최댓값 대비 %): 막대 위 (막대를 가리지 않게)
    const label = showValues
      ? this.valueLabelSvg(offsetX + barWidth / 2, 2 + labelSpace / 2, pct, fs)
      : '';

    const axis = `<line x1="${offsetX - 2}" y1="${baselineY}" x2="${offsetX + barWidth + 2}" y2="${baselineY}"
               stroke="#333" stroke-width="1.5"/>`;

    return `<svg xmlns="http://www.w3.org/2000/svg" width="${svgW}" height="${svgH}" viewBox="0 0 ${svgW} ${svgH}">
      ${axis}
      ${bar}
      ${gapBar}
      ${label}
    </svg>`;
  }

  /**
   * 파생 레이어 이름 — 유형과 필드를 넣어 여러 개를 구분할 수 있게 한다
   */
  buildDerivedName(sourceName, config) {
    const fields = config.fields || [];
    const fieldPart = fields.length
      ? `(${fields.slice(0, 2).join(', ')}${fields.length > 2 ? ' 외' : ''})`
      : '';
    return this.uniqueLayerName(
      `${sourceName}_도형표현_${this.getChartTypeLabel(config.chartType)}${fieldPart}`
    );
  }

  /**
   * 차트 유형 표시 라벨
   */
  getChartTypeLabel(chartType) {
    if (chartType === 'pie') return '파이';
    if (chartType === '100bar') return '100% 막대';
    return '막대';
  }

  /**
   * 도형표현도 제거 — 원본 또는 파생 layerId 모두 허용.
   * 원본 id를 주면 그 원본으로 만든 도형표현도를 모두 제거한다.
   * @returns {number} 제거한 레이어 수
   */
  removeChartMap(layerId) {
    const targets = this.sourceByDerived.has(layerId)
      ? [layerId]                      // 파생 레이어를 직접 지목
      : this.getDerivedIds(layerId);   // 원본에서 만든 것 전부

    // LayerManager에서 제거하면 이벤트 리스너가 오버레이/범례 정리
    targets.forEach(id => layerManager.removeLayer(id));
    return targets.length;
  }

  /**
   * 모든 도형표현도 제거
   */
  clearAll() {
    const derivedIds = Array.from(this.sourceByDerived.keys());
    derivedIds.forEach(id => layerManager.removeLayer(id));
  }

  /**
   * 폴리곤/포인트 레이어 목록 가져오기 — 벡터 소스가 있는 레이어만 (흐름·래스터 제외)
   */
  getCompatibleLayers() {
    return layerManager.getAllLayers().filter(layer => {
      return layer.geometryType && layer.source && layer.type !== 'heatmap' && layer.type !== 'chartmap';
    });
  }

  /**
   * 숫자 필드 목록 가져오기
   */
  getNumericFields(layerId) {
    const layerInfo = layerManager.getLayer(layerId);
    if (!layerInfo) return [];

    const source = layerInfo.olLayer.getSource();
    return collectNumericFields(source ? source.getFeatures() : []);
  }

  /**
   * 색상 팔레트 가져오기
   */
  getColors() {
    return this.colors;
  }
}

export const chartMapTool = new ChartMapTool();
